import EmptyState from "./EmptyState";
import { Badge } from "./StatusDot";
import { IconCalendar, IconVideo } from "./icons";
import { eventWhenLabel } from "../lib/agenda";
import type { CalendarEvent } from "../types";

/**
 * «Найближчі події» — the horizon column of «Сьогодні» (Vitalik, 2026-09-07).
 *
 * The old page kept this as a «ДАЛІ» / «НАЙБЛИЖЧЕ» strip of chips glued under
 * the day line, mixing three calendars of thing — meetings, goal deadlines and
 * subscription charges — into one row of badges nobody could scan. Deadlines
 * belong to the goals card and charges to the money card; what is left is what
 * this column is for: the next few things that will actually happen, in order,
 * each one saying when.
 *
 * `context.calendar_upcoming` already answers "what has not started yet",
 * ascending and across days (CONTRACTS.md §1.11), so there is no judgement to
 * make here beyond how many to show.
 */

/** Five is the column: enough to cover the week ahead, short enough to read. */
const SHOWN = 5;

const CALENDAR_URL = "https://calendar.google.com/";

interface UpcomingEventsProps {
  /**
   * Layout *and* display. This card deliberately does not set its own
   * `display`: «Сьогодні» drops the whole column below `lg` with
   * `hidden lg:flex`, and a `flex` written inside would win the cascade and
   * paint it on a phone anyway. Whatever is passed must say how it is shown.
   */
  className?: string;
  /** The summary's own today (Kyiv) — «Сьогодні» / «Завтра» are relative to it. */
  today: string;
  events: CalendarEvent[];
  /** False when Google is unconfigured or unauthorized: no events ≠ nothing planned. */
  available: boolean;
  /** Why the calendar is empty, when it is not simply empty. */
  note: string | null;
}

export default function UpcomingEvents({
  className = "flex",
  today,
  events,
  available,
  note,
}: UpcomingEventsProps) {
  const rows = events.slice(0, SHOWN);

  return (
    <section
      className={`jv-card flex-col gap-3 p-3 sm:p-4 ${className}`}
      aria-label="Найближчі події"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-fg">Найближчі події</h2>
        {rows.length > 0 && (
          <Badge className="jv-num shrink-0" title="Скільки подій показано">
            {rows.length}
          </Badge>
        )}
      </header>

      {rows.length === 0 ? (
        // One line, not a title over a note repeating it: the connector's own
        // words are the whole message when Google is the reason.
        <EmptyState
          compact
          icon="◷"
          title={available ? "Нічого не заплановано" : (note ?? "Календар не підключено")}
        />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((event) => (
            <li key={event.id} className="flex items-start gap-2.5 py-2 first:pt-0">
              <Glyph event={event} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-fg" title={event.title}>
                  {event.title}
                </div>
                <div className="jv-num text-micro text-muted">
                  {eventWhenLabel(event, today)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <a
        href={CALENDAR_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="jv-btn mt-auto justify-center"
      >
        Відкрити календар →
      </a>
    </section>
  );
}

/**
 * A call or an appointment. The video mark is the only distinction worth a
 * glyph here: one of these he opens in a tab, the other he has to be somewhere
 * for, and that changes what he does with the next ten minutes.
 *
 * The shape carries it, not a colour — the accent means focus and actions, and
 * a row that is merely a video call is neither.
 */
function Glyph({ event }: { event: CalendarEvent }) {
  const Mark = event.meet_url ? IconVideo : IconCalendar;
  return (
    <span className="mt-0.5 shrink-0 text-muted" title={event.meet_url ? "Відеозустріч" : undefined}>
      <Mark className="size-4" />
    </span>
  );
}
